import React, { useState, useMemo } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, addMonths, subMonths } from 'date-fns';
import { MdChevronLeft, MdChevronRight, MdClose } from 'react-icons/md';
import { getIconComponent } from '../utils/iconHelper';
import './TransactionCalendar.css';

function TransactionCalendar({ virtualTransactions, categories, accounts, getCategory, getAccount, formatCurrency }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  // Função para formatar o mês e ano em português
  const formatMonthYear = (date) => {
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${month} ${year}`;
  };

  // Navegar entre meses
  const handlePrevMonth = () => {
    setCurrentDate(subMonths(currentDate, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(addMonths(currentDate, 1));
  };

  // Calcular dias do mês
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
  
  // Calcular o primeiro dia da semana do mês (0 = domingo, 1 = segunda, etc.)
  const firstDayOfWeek = getDay(monthStart);

  // Agrupar transações por dia do mês
  const transactionsByDay = useMemo(() => {
    const grouped = {};
    
    virtualTransactions.forEach(transaction => {
      if (!transaction.is_active) return; // Só mostra transações ativas
      
      const day = transaction.day_of_month;
      if (!grouped[day]) {
        grouped[day] = [];
      }
      grouped[day].push(transaction);
    });
    
    return grouped;
  }, [virtualTransactions]);

  // Calcular total de receitas e despesas por dia
  const getDayTotals = (day) => {
    const transactions = transactionsByDay[day] || [];
    const income = transactions
      .filter(t => t.type === 'income')
      .reduce((sum, t) => sum + parseFloat(t.amount), 0);
    const expense = transactions
      .filter(t => t.type === 'expense')
      .reduce((sum, t) => sum + parseFloat(t.amount), 0);
    
    return { income, expense, total: income - expense, count: transactions.length };
  };

  // Abrir modal com detalhes do dia
  const handleDayClick = (day) => {
    const transactions = transactionsByDay[day];
    if (transactions && transactions.length > 0) {
      setSelectedDay(day);
      setShowModal(true);
    }
  };

  // Fechar modal
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedDay(null);
  };

  return (
    <div className="transaction-calendar">
      {/* Cabeçalho do calendário */}
      <div className="calendar-header">
        <button className="calendar-nav-btn" onClick={handlePrevMonth}>
          <MdChevronLeft size={24} />
        </button>
        <h2 className="calendar-title">
          {formatMonthYear(currentDate)}
        </h2>
        <button className="calendar-nav-btn" onClick={handleNextMonth}>
          <MdChevronRight size={24} />
        </button>
      </div>

      {/* Grid do calendário */}
      <div className="calendar-grid">
        {/* Cabeçalho dos dias da semana */}
        {weekDays.map(day => (
          <div key={day} className="calendar-weekday">
            {day}
          </div>
        ))}

        {/* Células vazias antes do primeiro dia do mês */}
        {Array.from({ length: firstDayOfWeek }).map((_, index) => (
          <div key={`empty-${index}`} className="calendar-day empty"></div>
        ))}

        {/* Dias do mês */}
        {daysInMonth.map(day => {
          const dayNumber = day.getDate();
          const dayTotals = getDayTotals(dayNumber);
          const hasTransactions = dayTotals.count > 0;

          return (
            <div
              key={dayNumber}
              className={`calendar-day ${hasTransactions ? 'has-transactions' : ''}`}
              onClick={() => hasTransactions && handleDayClick(dayNumber)}
            >
              <div className="day-number">{dayNumber}</div>
              
              {hasTransactions && (
                <div className="day-transactions">
                  <div className="transaction-count">{dayTotals.count}</div>
                  
                  <div className="day-totals">
                    {dayTotals.income > 0 && (
                      <div className="total-income">
                        +{formatCurrency(dayTotals.income)}
                      </div>
                    )}
                    {dayTotals.expense > 0 && (
                      <div className="total-expense">
                        -{formatCurrency(dayTotals.expense)}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Modal com detalhes das transações do dia */}
      {showModal && selectedDay && (
        <div className="calendar-modal-overlay" onClick={handleCloseModal}>
          <div className="calendar-modal" onClick={(e) => e.stopPropagation()}>
            <div className="calendar-modal-header">
              <h3>Transações do dia {selectedDay}</h3>
              <button className="calendar-modal-close" onClick={handleCloseModal}>
                <MdClose size={24} />
              </button>
            </div>

            <div className="calendar-modal-body">
              {transactionsByDay[selectedDay]?.map((transaction) => {
                const category = getCategory(transaction.category_id);
                const account = getAccount(transaction.account_id);
                const CategoryIcon = category ? getIconComponent(category.icon) : null;

                return (
                  <div
                    key={transaction.id}
                    className={`calendar-transaction-item ${transaction.type}`}
                  >
                    <div className="calendar-transaction-icon-wrapper">
                      <div
                        className="calendar-transaction-icon"
                        style={{ backgroundColor: category?.color || '#999' }}
                      >
                        {CategoryIcon && <CategoryIcon size={20} color="white" />}
                      </div>
                    </div>

                    <div className="calendar-transaction-content">
                      <div className="calendar-transaction-header">
                        <h4 className="calendar-transaction-description">
                          {transaction.description}
                        </h4>
                        <span className={`calendar-transaction-amount ${transaction.type}`}>
                          {transaction.type === 'income' ? '+' : '-'}
                          {formatCurrency(Math.abs(transaction.amount))}
                        </span>
                      </div>

                      <div className="calendar-transaction-meta">
                        <div className="calendar-transaction-detail">
                          <span className="detail-label">Categoria:</span>
                          <span className="detail-value">{category?.name || '-'}</span>
                        </div>
                        <div className="calendar-transaction-detail">
                          <span className="detail-label">Conta:</span>
                          <span className="detail-value">{account?.name || '-'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="calendar-modal-footer">
              <div className="calendar-modal-summary">
                <div className="summary-item income">
                  <span className="summary-label">Total Receitas:</span>
                  <span className="summary-value">
                    +{formatCurrency(getDayTotals(selectedDay).income)}
                  </span>
                </div>
                <div className="summary-item expense">
                  <span className="summary-label">Total Despesas:</span>
                  <span className="summary-value">
                    -{formatCurrency(getDayTotals(selectedDay).expense)}
                  </span>
                </div>
                <div className="summary-item total">
                  <span className="summary-label">Saldo:</span>
                  <span className={`summary-value ${getDayTotals(selectedDay).total >= 0 ? 'positive' : 'negative'}`}>
                    {formatCurrency(getDayTotals(selectedDay).total)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TransactionCalendar;
